<script>
import {
    assistantState,
    selectAssistantChat,
    renameAssistantChat,
} from "@app/store/assistantStore.svelte.js";
import { groupChatsByDate } from "@app/lib/assistant/historyGroups.js";
import { confirmDeleteAssistantChat } from "@app/lib/assistant/confirmDeleteChat.js";
import * as m from "@app/paraglide/messages.js";

let query = $state("");
let editingId = $state(null);
let editingTitle = $state("");

let groups = $derived(groupChatsByDate(assistantState.chats, { query }));

const GROUP_LABELS = {
    today: () => m.assistant_history_today(),
    yesterday: () => m.assistant_history_yesterday(),
    last7Days: () => m.assistant_history_last_7_days(),
    older: () => m.assistant_history_older(),
};

function startRename(chat) {
    editingId = chat.id;
    editingTitle = chat.title;
}

async function commitRename() {
    if (!editingId) return;
    const id = editingId;
    editingId = null;
    await renameAssistantChat(id, editingTitle);
}

function handleRenameKeydown(e) {
    if (e.key === "Enter") {
        e.preventDefault();
        commitRename();
    } else if (e.key === "Escape") {
        e.preventDefault();
        editingId = null;
    }
}

function focusOnMount(node) {
    node.focus();
    node.select();
}

</script>

<div class="assistant-history d-flex flex-column h-100 border-start">
    <div class="p-2 border-bottom">
        <div class="small fw-semibold mb-1">{m.assistant_history_heading()}</div>
        <input
            type="search"
            class="form-control form-control-sm"
            placeholder={m.assistant_history_search()}
            aria-label={m.assistant_history_search()}
            bind:value={query}
        />
    </div>
    <div class="flex-grow-1 overflow-auto py-1">
        {#if groups.length === 0}
            <p class="small text-muted px-2 mt-2">{m.assistant_history_empty()}</p>
        {/if}
        {#each groups as group (group.key)}
            <div class="small text-muted text-uppercase px-2 mt-2 mb-1 assistant-history-group">
                {GROUP_LABELS[group.key]()}
            </div>
            <ul class="list-unstyled mb-0">
                {#each group.chats as chat (chat.id)}
                    <li class={["assistant-history-item d-flex align-items-center px-2", chat.id === assistantState.activeChatId && "active"]}>
                        {#if editingId === chat.id}
                            <input
                                class="form-control form-control-sm"
                                aria-label={m.assistant_chat_title_label()}
                                bind:value={editingTitle}
                                onkeydown={handleRenameKeydown}
                                onblur={commitRename}
                                {@attach focusOnMount}
                            />
                        {:else}
                            <button
                                type="button"
                                class="btn btn-link btn-sm text-reset text-decoration-none text-start text-truncate flex-grow-1 px-0"
                                title={chat.title}
                                onclick={() => selectAssistantChat(chat.id)}
                                ondblclick={() => startRename(chat)}
                            >
                                {#if assistantState.streamingChatId === chat.id}
                                    <i class="bi bi-circle-fill text-primary assistant-running-dot me-1" title={m.assistant_history_running()}></i>
                                {/if}
                                {chat.title}
                            </button>
                            <div class="assistant-history-actions d-flex">
                                <button
                                    type="button"
                                    class="btn btn-link btn-sm text-reset p-0 px-1"
                                    title={m.assistant_rename_chat()}
                                    aria-label={m.assistant_rename_chat()}
                                    onclick={() => startRename(chat)}
                                >
                                    <i class="bi bi-pencil"></i>
                                </button>
                                <button
                                    type="button"
                                    class="btn btn-link btn-sm text-reset p-0 px-1"
                                    title={m.assistant_delete_chat()}
                                    aria-label={m.assistant_delete_chat()}
                                    onclick={() => confirmDeleteAssistantChat(chat)}
                                >
                                    <i class="bi bi-trash"></i>
                                </button>
                            </div>
                        {/if}
                    </li>
                {/each}
            </ul>
        {/each}
    </div>
</div>

<style>
    .assistant-history-group {
        font-size: 0.68rem;
        font-weight: 600;
        letter-spacing: 0.06em;
    }

    .assistant-history-item {
        position: relative;
        border-radius: var(--bs-border-radius);
        margin: 0 0.35rem 1px;
        min-height: 1.9rem;
        font-size: 0.85rem;
    }

    .assistant-history-item:hover {
        background-color: var(--bs-secondary-bg);
    }

    .assistant-history-item.active {
        background-color: rgba(var(--bs-primary-rgb), 0.15);
    }

    .assistant-history-item.active::before {
        content: "";
        position: absolute;
        left: 0;
        top: 0.35rem;
        bottom: 0.35rem;
        width: 3px;
        border-radius: 2px;
        background-color: var(--bs-primary);
    }

    .assistant-history-actions {
        visibility: hidden;
    }

    .assistant-history-item:hover .assistant-history-actions,
    .assistant-history-item:focus-within .assistant-history-actions {
        visibility: visible;
    }

    .assistant-running-dot {
        font-size: 0.5rem;
        vertical-align: middle;
    }
</style>
